/* global Map */
export const
    DUPLICATE_LIMIT = 3,
    DUPLICATE_WINDOW_MS = 5 * 60 * 1000,
    GLOBAL_LIMIT = 20,
    GLOBAL_WINDOW_MS = 60 * 1000,
    MAX_FINGERPRINTS = 100;

const
    UUID_VALUE = /\b[0-9a-f]{8}-[0-9a-f-]{27,}\b/gi,
    HEX_VALUE = /\b[0-9a-f]{12,}\b/gi,
    NUMBER_VALUE = /\b\d+\b/g,
    SPACE_VALUE = /\s+/g;

function normalizePart ( value ) {
    return String( value == null ? '' : value )
        .replace( UUID_VALUE, '[id]' )
        .replace( HEX_VALUE, '[id]' )
        .replace( NUMBER_VALUE, '#' )
        .replace( SPACE_VALUE, ' ' )
        .trim()
        .toLowerCase();
}

function getExceptionParts ( event ) {
    const values = event && event.exception && event.exception.values;
    const exception = values && values[0];

    if ( !exception ) {
        return [];
    }

    const frames = exception.stacktrace && exception.stacktrace.frames || [];
    const stack = frames.slice( -3 ).map( frame => [
        normalizePart( frame.filename ),
        normalizePart( frame.function )
    ].join( ':' ) ).join( '|' );

    return [exception.type, exception.value, stack];
}

export function createEventFingerprint ( event = {} ) {
    const explicitFingerprint = Array.isArray( event.fingerprint ) ? event.fingerprint : [];
    const parts = explicitFingerprint.length ? explicitFingerprint : getExceptionParts( event );

    if ( !parts.length ) {
        parts.push( event.message || 'unknown-event' );
    }

    return parts.map( normalizePart ).join( '|' );
}

export default class EventLimiter {
    constructor ( options = {} ) {
        this.now = options.now || Date.now;
        this.duplicateLimit = options.duplicateLimit || DUPLICATE_LIMIT;
        this.duplicateWindowMs = options.duplicateWindowMs || DUPLICATE_WINDOW_MS;
        this.globalLimit = options.globalLimit || GLOBAL_LIMIT;
        this.globalWindowMs = options.globalWindowMs || GLOBAL_WINDOW_MS;
        this.maxFingerprints = options.maxFingerprints || MAX_FINGERPRINTS;
        this.fingerprints = new Map();
        this.globalTimestamps = [];
        this.pendingSuppressions = new Map();
        this.evictedSuppressionCount = 0;
        this.evictedSuppressionFingerprintCount = 0;
    }

    evaluate ( event ) {
        const now = this.now();
        const fingerprint = createEventFingerprint( event );
        const entry = this.getEntry( fingerprint, now );

        this.pruneGlobalTimestamps( now );
        entry.timestamps = entry.timestamps.filter(
            timestamp => timestamp > now - this.duplicateWindowMs
        );

        if ( entry.timestamps.length >= this.duplicateLimit ) {
            entry.suppressed++;
            this.pendingSuppressions.set( fingerprint, entry.suppressed );
            entry.lastSeen = now;
            return {
                allowed: false,
                fingerprint,
                reason: 'duplicate',
                retryAt: entry.timestamps[0] + this.duplicateWindowMs
            };
        }

        if ( this.globalTimestamps.length >= this.globalLimit ) {
            entry.lastSeen = now;
            return {
                allowed: false,
                fingerprint,
                reason: 'volume',
                retryAt: this.globalTimestamps[0] + this.globalWindowMs
            };
        }

        const suppressedCount = entry.timestamps.length === 0 ?
            this.takeFingerprintSuppression( fingerprint, entry ) : 0;

        entry.timestamps.push( now );
        entry.lastSeen = now;
        this.globalTimestamps.push( now );

        return {allowed: true, fingerprint, suppressedCount};
    }

    getEntry ( fingerprint, now ) {
        this.pruneFingerprints( now );

        if ( !this.fingerprints.has( fingerprint ) ) {
            this.evictOldestFingerprint();
            this.fingerprints.set( fingerprint, {
                lastSeen: now,
                suppressed: 0,
                timestamps: []
            } );
        }

        return this.fingerprints.get( fingerprint );
    }

    pruneGlobalTimestamps ( now ) {
        this.globalTimestamps = this.globalTimestamps.filter(
            timestamp => timestamp > now - this.globalWindowMs
        );
    }

    pruneFingerprints ( now ) {
        this.fingerprints.forEach( ( entry, fingerprint ) => {
            const retentionWindow = entry.suppressed ? this.duplicateWindowMs * 2 : this.duplicateWindowMs;
            if ( entry.lastSeen <= now - retentionWindow ) {
                this.preserveSuppressionCount( fingerprint );
                this.fingerprints.delete( fingerprint );
            }
        } );
    }

    evictOldestFingerprint () {
        if ( this.fingerprints.size < this.maxFingerprints ) {
            return;
        }

        let oldestFingerprint;
        let oldestTimestamp = Infinity;
        this.fingerprints.forEach( ( entry, fingerprint ) => {
            if ( entry.lastSeen < oldestTimestamp ) {
                oldestFingerprint = fingerprint;
                oldestTimestamp = entry.lastSeen;
            }
        } );
        this.preserveSuppressionCount( oldestFingerprint );
        this.fingerprints.delete( oldestFingerprint );
    }

    takeFingerprintSuppression ( fingerprint, entry ) {
        const suppressedCount = this.pendingSuppressions.get( fingerprint ) || 0;
        this.pendingSuppressions.delete( fingerprint );
        entry.suppressed = 0;
        return suppressedCount;
    }

    preserveSuppressionCount ( fingerprint ) {
        const suppressedCount = this.pendingSuppressions.get( fingerprint ) || 0;

        if ( !suppressedCount ) {
            return;
        }

        this.evictedSuppressionCount += suppressedCount;
        this.evictedSuppressionFingerprintCount++;
        this.pendingSuppressions.delete( fingerprint );
    }

    hasPendingSuppressionSummary () {
        return this.evictedSuppressionCount > 0 || this.pendingSuppressions.size > 0;
    }

    takeSuppressionSummary () {
        const summary = {
            fingerprintCount: this.evictedSuppressionFingerprintCount,
            suppressedCount: this.evictedSuppressionCount
        };

        this.pendingSuppressions.forEach( ( suppressedCount, fingerprint ) => {
            summary.fingerprintCount++;
            summary.suppressedCount += suppressedCount;

            const entry = this.fingerprints.get( fingerprint );
            if ( entry ) {
                entry.suppressed = 0;
            }
        } );

        this.pendingSuppressions.clear();
        this.evictedSuppressionCount = 0;
        this.evictedSuppressionFingerprintCount = 0;

        return summary;
    }

    getFingerprintCount () {
        return this.fingerprints.size;
    }
}
